import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { StoreService } from '../../store.service';
import { DealerService } from '../../dealer.service';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { AiInsightSection, AiSectionConfig, parseAiInsightSections } from 'src/app/core/ai-summary-parser';
import { TranslationService } from 'src/app/core/translation.service';

/**
 * The backend builds the workbook and streams it back with a Content-Disposition
 * filename, exactly like /report/profit_lost/export. Change this one line if the
 * endpoint lands on a different path.
 */
const LANDING_PAGE_EXPORT_PATH = '/report/landing_page/export';

/**
 * One heading in the Landing Page cell. `expanded` is view state rather than
 * data — rows are rebuilt on every load, so a collapsed group reopens closed.
 */
interface UrlGroup {
  label: string;
  urls: string[];
  expanded: boolean;
}

/**
 * One product the endpoint found in the carts captured on this page. `name` is
 * resolved server-side against the 5s catalogue for the cart's country and falls
 * back to the id when the lookup finds nothing, so it is always safe to show.
 */
interface ProductChip {
  id: string;
  name: string;
}

interface LandingPageRow {
  page_id: string;
  url_external: string;
  /** 'studio' or the classic page editor — decides the /site vs /page segment the API already built into page_url. */
  editor: string;
  /** Products the endpoint found in the carts captured on this page. */
  products: ProductChip[];
  /** View state for the product chips, same reasoning as UrlGroup.expanded. */
  productsExpanded: boolean;
  total_visitor: number;
  total_order: number;
  total_revenue: number;
  checkout_conversion: number;
  abandoned_rate: number;
  /**
   * The page's own addresses and the external URL it was reached on, each under
   * its own heading. A slug answers on the store subdomain and on every verified
   * custom domain, so the list can hold several URLs; only the first shows until
   * the group is expanded.
   */
  urlGroups: UrlGroup[];
}

/**
 * The prompt asks for these English headings word for word, in every dashboard
 * language, and the card titles are translated here. The translated phrases are
 * only a fallback for when the model translates a heading anyway.
 */
const AI_SUMMARY_SECTIONS: AiSectionConfig[] = [
  {
    theme: 'overview',
    title: 'Executive Overview',
    icon: 'fi-rr-chart-pie-alt',
    phrases: ['executive overview', 'overview', '执行概览', '概览', '總覽', '執行概覽', '概覽', '总览', 'ikhtisar eksekutif', 'ikhtisar'],
  },
  {
    theme: 'performers',
    title: 'Top Performers & Revenue Drivers',
    icon: 'fi-rr-trophy',
    phrases: ['top performers & revenue drivers', 'top performers', 'top performing', 'revenue drivers', '表现最佳', '表現最佳', '最佳表现', '最佳表現', 'performa terbaik', 'halaman terbaik'],
  },
  {
    theme: 'friction',
    title: 'Friction & Drop-Off Points',
    icon: 'fi-rr-exclamation',
    phrases: ['friction & drop-off points', 'friction', 'drop-off points', 'drop off points', 'bottlenecks', '流失点', '流失點', '阻力', '摩擦', 'titik hambatan', 'hambatan'],
  },
  {
    theme: 'recommendations',
    title: 'Actionable Recommendations',
    icon: 'fi-rr-bulb',
    phrases: ['actionable recommendations', 'recommendations', 'action plan', 'key takeaways', '行动建议', '行動建議', '建议', '建議', 'rekomendasi'],
  },
];

/** Pages with fewer interactions than this are too thin to call underperforming. */
const AI_MIN_INTERACTIONS = 5;

@Component({
  selector: 'app-landing-page-report',
  templateUrl: './landing-page-report.component.html',
  styleUrls: ['./landing-page-report.component.scss'],
})
export class LandingPageReportComponent implements OnInit {
  bearer: string;
  storeId: string;

  isLoading = true;
  loadFailed = false;

  isExporting = false;
  exportFailed = false;

  /** AI Assistant / Summary state */
  isGeneratingAiSummary = false;
  aiSummaryContent = '';
  aiSummaryError: string | null = null;
  showAiSummaryCard = false;
  isCopied = false;
  enableAIAssistant = false;
  /** Credits one AI summary costs, from the backend (AI_REPORT_SUMMARY_CREDIT); null until loaded or when not charged. */
  aiTokenCost: number | null = null;
  /** Purpose of the clicked AI button (its data-ai-purpose), sent to Mixpanel as ai_purpose. */
  aiPurpose = 'generate summary';
  parsedSections: AiInsightSection[] = [];
  private copyTimeout: any;

  /** Every row returned by the API, normalised. */
  rows: LandingPageRow[] = [];
  /** Rows left after the search box is applied, sorted. */
  filteredRows: LandingPageRow[] = [];

  searchTerm = '';

  /**
   * How many product chips a row shows before the "show more" pill. Three, not
   * more: these are full catalogue names in a narrow column, not short ids.
   */
  readonly PRODUCT_PREVIEW = 3;

  SORT_OPTIONS = [
    { display: 'Revenue', value: 'total_revenue' },
    { display: 'Interaction', value: 'total_visitor' },
    { display: 'Orders', value: 'total_order' },
    { display: 'Checkout Conversion', value: 'checkout_conversion' },
    { display: 'Abandoned Rate', value: 'abandoned_rate' },
  ];
  selectedSort = this.SORT_OPTIONS[0];

  summary = {
    revenue: 0,
    visitors: 0,
    orders: 0,
    conversion: 0,
    abandonedRate: 0,
    landingPages: 0,
  };

  /**
   * What each summary card actually counts, shown on the info icon beside its
   * label. Worth spelling out because the two rates are derived from the totals
   * rather than averaged across the rows — averaging per-page rates would let a
   * page with three visitors weigh as much as one with three thousand.
   */
  STAT_HINTS = {
    revenue: 'Total paid order value across the landing pages in view.',
    visitors: 'Total user interactions and actions performed on these landing pages.',
    orders: 'Sessions that ended in a paid order.',
    conversion: 'Orders divided by interactions, across all the rows in view.',
    abandonedRate: 'Interactions that left without a paid order, across all the rows in view.',
  };

  /** Highest revenue in the current view, used to scale the share bars. */
  maxRevenue = 0;

  currentPage = 1;
  pageSize = 20;

  /** Identifies the newest in-flight report request; see loadReport. */
  private loadToken = 0;

  constructor(
    private dealerService: DealerService,
    private svc: StoreService,
    private utils: UtilitiesService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.bearer = localStorage.getItem('bearer');
    this.storeId = localStorage.getItem('store_id');
    this.route.queryParams.subscribe((params) => {
      const q = params['search'] || params['page_url'] || params['q'] || '';
      if (q) {
        this.searchTerm = q;
      }
      if (this.rows.length > 0) {
        this.applyFilters();
      }
    });
    this.loadAiCreditState();
    this.loadReport();
  }

  loadReport(): void {
    this.isLoading = true;
    this.loadFailed = false;

    // Retry can be pressed again while a fetch is still out, so only the newest
    // response is allowed to land.
    const token = ++this.loadToken;

    this.svc.getLandingPageReport(this.bearer, this.storeId).subscribe(
      (res: any) => {
        if (token !== this.loadToken) return;
        this.rows = this.extractRows(res).map((row) => this.normaliseRow(row));
        this.applyFilters();
        this.isLoading = false;
      },
      () => {
        if (token !== this.loadToken) return;
        this.rows = [];
        this.filteredRows = [];
        this.isLoading = false;
        this.loadFailed = true;
      }
    );
  }

  /** The API wraps the list differently across report endpoints — accept the known shapes. */
  private extractRows(res: any): any[] {
    const candidates = [res?.data?.data, res?.data?.report, res?.data, res?.report, res];
    return candidates.find((value) => Array.isArray(value)) ?? [];
  }

  private normaliseRow(row: any): LandingPageRow {
    return {
      page_id: `${row?.page_id ?? ''}`,
      url_external: `${row?.url_external ?? ''}`,
      editor: `${row?.editor ?? ''}`,
      products: this.toProducts(row),
      productsExpanded: false,
      total_visitor: this.toNumber(row?.total_visitor),
      total_order: this.toNumber(row?.total_order),
      total_revenue: this.toNumber(row?.total_revenue),
      checkout_conversion: this.toNumber(row?.checkout_conversion),
      abandoned_rate: this.toNumber(row?.abandoned_rate),
      urlGroups: this.toUrlGroups(row),
    };
  }

  private toNumber(value: any): number {
    const parsed = parseFloat(`${value ?? 0}`.replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * The endpoint returns the ids and their resolved names as two parallel lists,
   * both built from the same id set, so they line up index for index.
   */
  private toProducts(row: any): ProductChip[] {
    const ids = this.toStringList(row?.product_id);

    // Responses from before the catalogue lookup landed carry one comma-separated
    // string of ids under `product` and no names at all.
    if (!ids.length) {
      return this.toStringList(`${row?.product ?? ''}`.split(',')).map((id) => ({
        id,
        name: id,
      }));
    }

    const names = this.toStringList(row?.product_name);
    return ids.map((id, index) => ({ id, name: names[index] || id }));
  }

  /**
   * Accepts a list, a lone value, or nothing. A cart item without a recognisable
   * product id leaves a literal "null" in the concatenated list, so that spelling
   * is dropped alongside the blanks rather than shown as a product.
   */
  private toStringList(raw: any): string[] {
    return (Array.isArray(raw) ? raw : [raw])
      .map((value) => `${value ?? ''}`.trim())
      .filter((value) => !!value && value.toLowerCase() !== 'null');
  }

  /**
   * The page's own addresses and the address the visitor actually arrived on are
   * different destinations, so the cell keeps them under separate headings. Each
   * shows only when the row carries it — a row with one heading is normal.
   */
  private toUrlGroups(row: any): UrlGroup[] {
    const groups: UrlGroup[] = [];
    // An external URL is often one of the resolved addresses too. Listing it
    // twice under two headings would read as a bug, so later groups only keep
    // what earlier ones did not already show.
    const seen = new Set<string>();

    const add = (label: string, raw: any): void => {
      const urls = this.toUrlList(raw).filter((url) => !seen.has(url));
      if (!urls.length) return;
      urls.forEach((url) => seen.add(url));
      groups.push({ label, urls, expanded: false });
    };

    add('Page Url', row?.page_url);
    add('External URL', row?.url_external);

    return groups;
  }

  /** Accepts a list, a lone string from older responses, or nothing. */
  private toUrlList(raw: any): string[] {
    return (Array.isArray(raw) ? raw : [raw])
      .map((url) => `${url ?? ''}`.trim())
      .filter((url) => !!url);
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();

    this.filteredRows = this.rows
      // Any URL in any group counts, including ones behind a collapsed group, and
      // so does a product — by name, which is what the chip shows, and by id,
      // which is what the dealer has in front of them elsewhere in the dashboard.
      .filter(
        (row) =>
          !term ||
          row.urlGroups.some((group) =>
            group.urls.some((url) => url.toLowerCase().includes(term))
          ) ||
          row.products.some(
            (product) =>
              product.name.toLowerCase().includes(term) ||
              product.id.toLowerCase().includes(term)
          )
      )
      .sort((a, b) => b[this.selectedSort.value] - a[this.selectedSort.value]);

    this.maxRevenue = this.filteredRows.reduce(
      (max, row) => Math.max(max, row.total_revenue),
      0
    );

    const visitors = this.filteredRows.reduce((sum, row) => sum + row.total_visitor, 0);
    const orders = this.filteredRows.reduce((sum, row) => sum + row.total_order, 0);
    // Recomputed from the totals rather than averaged over the rows, so a page
    // with a handful of visitors cannot pull the headline rate around.
    const conversion = visitors > 0 ? (orders / visitors) * 100 : 0;

    this.summary = {
      revenue: this.filteredRows.reduce((sum, row) => sum + row.total_revenue, 0),
      visitors,
      orders,
      conversion,
      abandonedRate: visitors > 0 ? 100 - conversion : 0,
      landingPages: this.filteredRows.length,
    };

    this.currentPage = 1;
  }

  selectSort(option: { display: string; value: string }): void {
    this.selectedSort = option;
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilters();
  }

  /**
   * The workbook is built server-side and streamed back, so the search box
   * travels as a query param rather than filtering the rows on screen.
   */
  downloadExcel(): void {
    if (!this.filteredRows.length || this.isExporting) return;

    const param = {
      store_id: this.storeId,
      search: this.searchTerm.trim(),
      type: 'excel',
    };

    this.isExporting = true;
    this.exportFailed = false;

    this.svc
      .downloadSpreadsheet(this.bearer, LANDING_PAGE_EXPORT_PATH, param)
      .catch((err) => {
        this.exportFailed = true;
        console.error('[landing-page-report] export failed', err);
      })
      .then(() => (this.isExporting = false));
  }

  get pagedRows(): LandingPageRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredRows.slice(start, start + this.pageSize);
  }

  get start(): number {
    return (this.currentPage - 1) * this.pageSize;
  }

  get end(): number {
    return Math.min(this.start + this.pageSize, this.filteredRows.length);
  }

  formatAmount(value: number): string {
    return (value ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatCount(value: number): string {
    return (value ?? 0).toLocaleString();
  }

  /** Rates arrive already rounded to two places; keep them there when they are summed. */
  formatRate(value: number): string {
    return `${(value ?? 0).toFixed(2)}%`;
  }

  /** Width of the share bar under each revenue figure. */
  revenueShare(row: LandingPageRow): number {
    if (!this.maxRevenue) return 0;
    return Math.max(2, Math.round((row.total_revenue / this.maxRevenue) * 100));
  }

  /** Rate bars are already a percentage, but a stray value outside 0-100 would overflow the track. */
  clampRate(value: number): number {
    return Math.min(100, Math.max(0, value ?? 0));
  }

  /**
   * A chip is clipped to its column, so the full name lives on the title. The id
   * is only worth appending when the lookup actually resolved a name — otherwise
   * it would read as the same value twice.
   */
  productTitle(product: ProductChip): string {
    return product.name === product.id ? product.id : `${product.name} (${product.id})`;
  }

  /** Strips the protocol so the path stays readable on a narrow screen and in chips. */
  displayUrl(url?: string): string {
    if (!url) return '-';
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  /** Loads the AI Summary price (from the backend config) and whether the button can be used. */
  loadAiCreditState(): void {
    this.dealerService.getAiCreditState('AI_REPORT_SUMMARY_CREDIT').subscribe(({ enabled, tokenCost }) => {
      this.enableAIAssistant = enabled;
      this.aiTokenCost = tokenCost;
    });
  }

  /**
   * Prepares the report data for the AI prompt (GENERATE_LANDING_PAGE_REPORT_SUMMARY).
   * Every derived figure — shares, multiples, missed orders, revenue opportunity — is
   * worked out here so the model only has to explain numbers, never calculate them.
   * Amounts carry no currency symbol: the page shows none, and rows can come from
   * stores in different countries. The labels are referenced by the prompt template.
   */
  prepareReportSummaryPayload(): string {
    const rows = this.filteredRows;
    const { revenue, visitors, orders, conversion, abandonedRate } = this.summary;
    const aov = orders > 0 ? revenue / orders : 0;

    const money = (v: number) =>
      (v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const count = (v: number) => Math.round(v ?? 0).toLocaleString('en-US');
    // Estimates are rounded to whole amounts: "about 3,008.44" claims precision they do not have
    const estimate = (v: number) => Math.round(v ?? 0).toLocaleString('en-US');
    const pct = (v: number) => `${(v ?? 0).toFixed(2)}%`;
    const share = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '0%');
    const urlOf = (r: LandingPageRow) => r.urlGroups[0]?.urls[0] || r.url_external || r.page_id;
    const productsOf = (r: LandingPageRow) =>
      r.products.slice(0, 3).map((p) => p.name).join(', ') + (r.products.length > 3 ? ` (+${r.products.length - 3} more)` : '') || 'none recorded';

    const pagesWithTraffic = rows.filter((r) => r.total_visitor > 0);
    const trafficNoOrders = pagesWithTraffic.filter((r) => r.total_order === 0);
    const pagesWithOrders = rows.filter((r) => r.total_order > 0);

    // Top performers: earn the most, ties broken by orders. A page converting below the store
    // average is an underperformer even when it earns, so it is left out here unless it is the
    // only earner worth naming (the single highest-revenue page always stays).
    const byRevenue = [...pagesWithOrders].sort(
      (a, b) => b.total_revenue - a.total_revenue || b.total_order - a.total_order
    );
    const topPages = byRevenue
      .filter((r, i) => i === 0 || r.checkout_conversion >= conversion)
      .slice(0, 3);

    const topLines = topPages.map((r, i) => {
      const multiple = conversion > 0 ? `${(r.checkout_conversion / conversion).toFixed(1)}x the store average` : 'n/a';
      return `${i + 1}. URL: ${urlOf(r)} | Revenue: ${money(r.total_revenue)} (${share(r.total_revenue, revenue)} of total revenue)`
        + ` | Paid orders: ${count(r.total_order)} | Interactions: ${count(r.total_visitor)}`
        + ` | Conversion: ${pct(r.checkout_conversion)} (${multiple}) | Products: ${productsOf(r)}`;
    });

    // Underperformers: enough traffic to judge, converting below the store average.
    // "Missed orders" is what the page would add at the store-average conversion rate.
    const underperformers = pagesWithTraffic
      .filter((r) => r.total_visitor >= AI_MIN_INTERACTIONS && r.checkout_conversion < conversion)
      .map((r) => {
        const missedOrders = Math.max(0, (r.total_visitor * (conversion - r.checkout_conversion)) / 100);
        return { r, missedOrders, missedRevenue: missedOrders * aov };
      })
      .sort((a, b) => b.missedRevenue - a.missedRevenue || b.r.total_visitor - a.r.total_visitor);

    // With no orders anywhere there is no average to compare against, so fall back to the busiest pages.
    const frictionSource = conversion > 0
      ? underperformers.slice(0, 3)
      : [...pagesWithTraffic]
          .sort((a, b) => b.total_visitor - a.total_visitor)
          .slice(0, 3)
          .map((r) => ({ r, missedOrders: 0, missedRevenue: 0 }));

    const frictionLines = frictionSource.map(({ r, missedOrders, missedRevenue }, i) => {
      const opportunity = conversion > 0
        ? ` | Missed paid orders vs store average: about ${count(Math.max(1, missedOrders))}`
          + ` | Revenue opportunity: about ${estimate(missedRevenue)}`
        : '';
      return `${i + 1}. URL: ${urlOf(r)} | Interactions: ${count(r.total_visitor)} (${share(r.total_visitor, visitors)} of all interactions)`
        + ` | Paid orders: ${count(r.total_order)} | Conversion: ${pct(r.checkout_conversion)}`
        + ` | Abandoned rate: ${pct(r.abandoned_rate)}${opportunity} | Products: ${productsOf(r)}`;
    });

    const totalOpportunity = underperformers.reduce((sum, u) => sum + u.missedRevenue, 0);
    const topShare = topPages.length ? share(topPages[0].total_revenue, revenue) : '0%';

    const lines = [
      'OVERVIEW',
      `Landing pages in view: ${count(rows.length)}`,
      `Pages with traffic: ${count(pagesWithTraffic.length)}`,
      `Pages with traffic but no paid orders: ${count(trafficNoOrders.length)}`,
      `Total revenue: ${money(revenue)}`,
      `Total interactions: ${count(visitors)}`,
      `Total paid orders: ${count(orders)}`,
      `Store average conversion rate: ${pct(conversion)} (paid orders per interaction)`,
      `Abandoned rate: ${pct(abandonedRate)}`,
      `Average order value: ${money(aov)}`,
      `Share of revenue from the top page: ${topShare}`,
      `Revenue opportunity if every underperforming page reached the store average: about ${estimate(totalOpportunity)}`,
      `Small sample: ${visitors < 100 ? 'yes - fewer than 100 interactions, treat patterns as early signals' : 'no'}`,
    ];
    if (this.searchTerm.trim()) {
      lines.push(`Filtered by search: "${this.searchTerm.trim()}"`);
    }

    return [
      lines.join('\n'),
      `TOP PAGES BY REVENUE\n${topLines.length ? topLines.join('\n') : 'None - no page has a paid order yet'}`,
      `${conversion > 0 ? 'UNDERPERFORMING PAGES (enough traffic, converting below the store average)' : 'BUSIEST PAGES (no paid orders yet anywhere)'}\n`
        + `${frictionLines.length ? frictionLines.join('\n') : 'None'}`,
    ].join('\n\n');
  }

  /** Sends the Mixpanel AI Generate event; ai_purpose comes from the clicked button's data-ai-purpose. */
  private trackAiGenerate(event: 'AI Generate Success' | 'AI Generate Fail', errorResponse?: string): void {
    this.svc.trackAiGenerateEvent(event, {
      aiPurpose: this.aiPurpose,
      storeId: this.storeId,
      token: this.aiTokenCost || 0,
      errorResponse,
    });
  }

  /** Triggers the AI Assistant to analyze and summarize the report. */
  generateAiSummary(event?: Event): void {
    if (this.isGeneratingAiSummary) return;
    this.aiPurpose = (event?.currentTarget as HTMLElement)?.dataset?.aiPurpose || this.aiPurpose;

    if (!this.filteredRows.length) {
      this.utils?.notify?.showInfo?.(
        'No landing page data available to summarize.',
        'Notice'
      );
      this.trackAiGenerate('AI Generate Fail', 'No landing page data available to summarize.');
      return;
    }

    this.isGeneratingAiSummary = true;
    this.aiSummaryError = null;
    this.showAiSummaryCard = true;
    this.isCopied = false;

    const reportData = this.prepareReportSummaryPayload();

    const params: any = {
      type: 'GENERATE_LANDING_PAGE_REPORT_SUMMARY',
      report_data: reportData,
      // Middleware writes the summary in this dashboard language; headings stay English for parseAiSummary()
      language_code: this.translationService.getLanguageCode(),
      desc: 'AI Landing Page Report Summary Generation',
    };

    this.svc.getAIGeneratedContent(params).subscribe(
      (res: any) => {
        this.isGeneratingAiSummary = false;
        let content = res?.data;
        if (typeof content === 'object') {
          content = JSON.stringify(content, null, 2);
        }
        this.aiSummaryContent = content || 'No summary generated.';
        this.parseAiSummary();
        this.loadAiCreditState();
        this.trackAiGenerate('AI Generate Success');
      },
      (err: any) => {
        this.isGeneratingAiSummary = false;
        console.error('[landing-page-report] AI generation failed', err);
        const serverMsg =
          err?.error?.message ||
          err?.error?.data?.error ||
          err?.error?.data?.message ||
          err?.error?.error;
        const errorMsg =
          (typeof serverMsg === 'string' && serverMsg ? serverMsg : null) ||
          this.utils?.getErrorMessageWithRefId?.(
            err,
            'Unable to generate AI summary at this time. Please check your AI Assistant credits or try again.'
          ) ||
          'Unable to generate AI summary at this time. Please check your AI Assistant credits or try again.';
        this.aiSummaryError = errorMsg;
        this.trackAiGenerate('AI Generate Fail', errorMsg);
      }
    );
  }

  /** Parses raw AI text into clean, structured sections for modern card rendering. */
  parseAiSummary(): void {
    // splitNotes: "Label: 62% - what it means" renders the figure bold and the explanation regular
    this.parsedSections = parseAiInsightSections(this.aiSummaryContent, AI_SUMMARY_SECTIONS, { splitNotes: true });
  }

  /** Copies generated AI summary text to clipboard. */
  copyAiSummary(): void {
    if (!this.aiSummaryContent) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(this.aiSummaryContent).then(() => {
        this.isCopied = true;
        this.utils?.notify?.showSuccess?.(
          'AI Summary has been copied to clipboard',
          'Copied!'
        );
        clearTimeout(this.copyTimeout);
        this.copyTimeout = setTimeout(() => {
          this.isCopied = false;
        }, 3000);
      });
    } else {
      this.utils?.copyToClipboard?.(this.aiSummaryContent, 'AI Summary');
    }
  }

  /** Closes/hides the AI summary card. */
  dismissAiSummary(): void {
    this.showAiSummaryCard = false;
  }
}
